import type {
  CreateGenerationRequest,
  CreateLyricsDraftRequest,
  CurrentUser,
  GenerationView,
  LyricsDraftView,
  Plan
} from "@sonic-five-elements/contracts";

const isLocalHost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
export const API_BASE = (
  isLocalHost
    ? (import.meta.env.VITE_API_BASE_URL || "http://localhost:8787")
    : "https://api.sonicfiveelements.com"
).replace(/\/$/, "");
const MEDIA_RELEASE = "20260819.2";

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

export interface CreditLotView {
  id: string;
  sourceType: "free_preview" | "order" | "admin";
  granted: number;
  remaining: number;
  archiveDays: 0 | 30 | 90;
  eligibleMode: "instrumental" | "song" | null;
  expiresAt: string;
  createdAt: string;
}

export interface OrderView {
  id: string;
  paypalOrderId: string | null;
  paypalCaptureId: string | null;
  amountUsd: number;
  status: string;
  createdAt: string;
  completedAt: string | null;
  planId: string;
  planName: string;
  credits: number;
}

export type AnalyticsEventName = "landing_view" | "sample_play" | "paypal_checkout_started" |
  "temporary_audio_played" | "music_played" | "music_downloaded";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      signal: init.signal || controller.signal,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init.headers || {}) }
    });
    if (response.status === 204) return undefined as T;
    const data = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } } & T;
    if (!response.ok) throw new ApiError(data.error?.code || "REQUEST_FAILED", data.error?.message || "Request failed.", response.status);
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError("NETWORK_UNAVAILABLE", "The service is temporarily unavailable. Please try again.", 0);
  } finally {
    clearTimeout(timeout);
  }
}

export const api = {
  plans: () => request<Plan[]>("/plans"),
  me: () => request<{ user: CurrentUser | null }>("/me"),
  googleLogin: (credential: string) => request<CurrentUser>("/auth/google", { method: "POST", body: JSON.stringify({ credential }) }),
  requestEmailLogin: (email: string, turnstileToken: string) => request<{
    accepted: true;
    requestId: string;
    emailHint: string;
    expiresInSeconds: number;
  }>("/auth/email/request", { method: "POST", body: JSON.stringify({ email, turnstileToken }) }),
  verifyEmailCode: (requestId: string, code: string) => request<CurrentUser>("/auth/email/code/verify", { method: "POST", body: JSON.stringify({ requestId, code }) }),
  verifyEmailLogin: (token: string) => request<CurrentUser>("/auth/email/verify", { method: "POST", body: JSON.stringify({ token }) }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  createGeneration: (payload: CreateGenerationRequest) => request<{ id: string; status: string }>("/generations", { method: "POST", body: JSON.stringify(payload) }),
  createLyricsDraft: (payload: CreateLyricsDraftRequest) => request<{ id: string; status: string }>("/lyrics/drafts", { method: "POST", body: JSON.stringify(payload) }),
  lyricsDraft: (id: string) => request<LyricsDraftView>(`/lyrics/drafts/${encodeURIComponent(id)}`),
  generation: (id: string) => request<GenerationView>(`/generations/${encodeURIComponent(id)}`),
  library: () => request<{ items: GenerationView[] }>("/library"),
  downloadMusic: (path: string, jobId: string) => downloadMusic(path, jobId),
  credits: () => request<{ balance: number; lots: CreditLotView[]; ledger: unknown[] }>("/credits"),
  orders: () => request<{ items: OrderView[] }>("/orders"),
  track: (name: AnalyticsEventName, details: {
    jobId?: string;
    planId?: string;
    properties?: Record<string, string | number | boolean | null>;
  } = {}) => request<void>("/events", { method: "POST", body: JSON.stringify({ name, ...details }) }).catch(() => undefined),
  createPayPalOrder: (planId: string) => request<{ orderId: string }>("/paypal/orders", { method: "POST", body: JSON.stringify({ planId }) }),
  capturePayPalOrder: (orderId: string) => request<{ status: string; creditsAdded?: number }>(`/paypal/orders/${encodeURIComponent(orderId)}/capture`, { method: "POST" })
};

async function downloadMusic(path: string, jobId: string): Promise<void> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 60_000);
  try {
    const response = await fetch(mediaUrl(path)!, {
      credentials: "include",
      signal: controller.signal,
      cache: "no-store"
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
      throw new ApiError(data.error?.code || "DOWNLOAD_FAILED", data.error?.message || "The MP3 could not be downloaded.", response.status);
    }
    const blob = await response.blob();
    if (!blob.size || !blob.type.startsWith("audio/")) {
      throw new ApiError("INVALID_AUDIO_RESPONSE", "The server did not return a valid MP3 file.", 502);
    }
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `five-tone-${jobId}.mp3`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError("DOWNLOAD_FAILED", "The MP3 download failed. Please try again.", 0);
  } finally {
    clearTimeout(timeout);
  }
}

export function mediaUrl(path: string | null): string | undefined {
  if (!path) return undefined;
  const separator = path.includes("?") ? "&" : "?";
  return `${API_BASE}${path}${separator}v=${MEDIA_RELEASE}`;
}
